<script setup lang="ts">
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';
import { storeToRefs } from 'pinia';
import { DataTable, Column } from 'primevue';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted } from 'vue';

const storageAreaStore = useStorageAreaStore();
const storageAreas = storeToRefs(storageAreaStore).storageAreas;
const productStore = useProductStore();
const refreshProductStore = useRefereshProductStore();
const translations = useTranslations();
const languageStore = useLanguageStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const refreshProductGroupStore = useRefereshProductGroupStore();

const loading = computed(
  () => refreshStorageAreaStore.loading || refreshProductStore.loading || refreshProductGroupStore.loading,
);

onMounted(async () => {
  void Promise.all([
    refreshStorageAreaStore.initialize(),
    refreshProductStore.initialize(),
    refreshProductGroupStore.initialize(),
  ]);
});

const products = computed<ProductTableRow[]>(() => {
  const rows: ProductTableRow[] = [];

  for (const storageArea of storageAreas.value) {
    if (!storageArea.logYardData) continue;

    for (const content of storageArea.logYardData.content) {
      const product = productStore.products.find((p) => p.id === content.productId);
      if (!product) continue;

      const productName = product.name;
      const existing = rows.find((row) => row.productName === productName);
      if (existing) {
        existing.numberOfLogs += content.numberOfLogs;
        existing.volume += content.volume;
      } else {
        rows.push({
          productName: productName,
          numberOfLogs: content.numberOfLogs,
          volume: content.volume,
        });
      }
    }
  }

  return rows.sort((a, b) => {
    if (a.productName === translations.value.generic.unknown) return 1;
    if (b.productName === translations.value.generic.unknown) return -1;
    return a.productName.localeCompare(b.productName);
  });
});

interface ProductTableRow {
  productName: string;
  numberOfLogs: number;
  volume: number;
}

function getVolume(row: ProductTableRow) {
  return languageStore.formatNumber(row.volume, 1);
}

function getNumberOfLogs(row: ProductTableRow) {
  return languageStore.formatNumber(row.numberOfLogs, 0);
}

const totalLogs = computed(() => {
  return languageStore.formatNumber(
    products.value.reduce((sum, row) => sum + row.numberOfLogs, 0),
    0,
  );
});

const totalVolume = computed(() => {
  return languageStore.formatNumber(
    products.value.reduce((sum, row) => sum + row.volume, 0),
    0,
  );
});
</script>

<template>
  <DataTable class="product-table" :value="products" data-key="id" scrollable :loading="loading" scrollHeight="flex"
    stripedRows>
    <template #header>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-xl font-bold">{{ translations.dashboard.productTable.title }}</span>
      </div>
    </template>
    <template #empty>{{ translations.generic.noData }}</template>
    <template #loading>{{ translations.generic.loadingTableData }}</template>
    <Column field="productName" :header="translations.generic.name" sortable>
      <template #footer>
        <strong>{{ translations.generic.total }}</strong>
      </template>
    </Column>
    <Column :field="getNumberOfLogs" :header="translations.generic.numberOfLogs" sortable>
      <template #footer>
        <strong>{{ totalLogs }}</strong>
      </template>
    </Column>
    <Column :field="getVolume" :header="translations.generic.volume" sortable>
      <template #footer>
        <strong>{{ totalVolume }}</strong>
      </template>
    </Column>
  </DataTable>
</template>

<style scoped>
:deep(.p-datatable-header div) {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
}
</style>
